/**
 * connectors.js — the TravelTrucks OS connector registry (single source of truth).
 *
 * One entry per integration Col can connect. The Admin → Connectors page renders
 * these as cards; each card's logo is the service's own mark from
 * /brand/connectors/<file>.svg (copied from the kit's connector-logos set — we
 * never invent icons). Status + account label come from GET /api/admin/connectors.
 *
 * Adding a connector? Add an entry here and drop its logo in
 * frontend/public/brand/connectors/. The page and the backend key off `id`.
 */

// Card grouping shown on the Connectors page, in display order.
export const CONNECTOR_CATEGORIES = [
  { key: 'money',     label: 'Money',     blurb: 'Invoices, payments and the cash picture' },
  { key: 'work',      label: 'Work',      blurb: 'Jobs, timesheets and work-in-progress' },
  { key: 'growth',    label: 'Growth',    blurb: 'Enquiries, CRM, social and campaigns' },
  { key: 'comms',     label: 'Comms',     blurb: 'Inbox, calendar and the day sheet' },
  { key: 'assistant', label: 'Assistant', blurb: 'The 5pm debrief and real-world capture' },
];

const LOGO = (file) => `/brand/connectors/${file}`;

/**
 * authType:
 *   'oauth'          → popup/redirect to the service, callback stores the token
 *   'webhook_token'  → the service POSTs to us; card shows the webhook URL + a token field
 *   'apikey'         → secure key field(s) the owner pastes
 */
export const CONNECTORS = [
  {
    id: 'xero',
    name: 'Xero',
    blurb: 'Money tiles, unpaid & overdue, chase list',
    powers: 'Money · Xero',
    logo: LOGO('xero.svg'),
    color: '#13B5EA',
    category: 'money',
    authType: 'oauth',
    connectPath: '/api/admin/connectors/xero/connect',
    statusKey: 'xero',
    readOnly: true, // Xero is never written to
    docsUrl: 'https://developer.xero.com',
  },
  {
    id: 'stripe',
    name: 'Stripe',
    blurb: 'Payments feed and deposits',
    powers: 'Money · Stripe',
    logo: LOGO('stripe.svg'),
    color: '#635BFF',
    category: 'money',
    authType: 'apikey',
    connectPath: '/api/admin/connectors/stripe/connect',
    statusKey: 'stripe',
    keyFields: [{ name: 'secret_key', label: 'Secret key (sk_live_… or sk_test_…)', secret: true }],
    docsUrl: 'https://dashboard.stripe.com/apikeys',
  },
  {
    id: 'workflowmax',
    name: 'Workflow Max',
    blurb: 'Jobs, timesheets and WIP',
    powers: 'Work · Workflow Max',
    logo: LOGO('workflowmax.svg'),
    color: '#2D9CDB',
    category: 'work',
    authType: 'oauth',
    connectPath: '/api/admin/connectors/workflowmax/connect',
    statusKey: 'workflowmax',
    docsUrl: 'https://www.workflowmax.com/api',
  },
  {
    id: 'ghl',
    name: 'GoHighLevel',
    blurb: 'Enquiries / CRM, social, campaigns',
    powers: 'Growth · GoHighLevel',
    logo: LOGO('gohighlevel.svg'),
    color: '#155EEF',
    category: 'growth',
    authType: 'apikey',
    connectPath: '/api/admin/connectors/ghl/connect',
    statusKey: 'ghl',
    keyFields: [
      { name: 'api_key', label: 'API key', secret: true },
      { name: 'location_id', label: 'Location ID', secret: false },
    ],
    docsUrl: 'https://highlevel.stoplight.io',
  },
  {
    id: 'microsoft',
    name: 'Microsoft 365',
    blurb: 'Inbox + calendar / run sheet',
    powers: 'Comms · Microsoft 365',
    logo: LOGO('microsoft365.svg'),
    color: '#F1F2F3',
    category: 'comms',
    authType: 'oauth',
    connectPath: '/api/admin/connectors/microsoft/connect',
    statusKey: 'microsoft',
    docsUrl: 'https://learn.microsoft.com/graph',
  },
  {
    id: 'fieldy',
    name: 'Fieldy',
    blurb: 'The 5pm debrief',
    powers: 'Assistant · Fieldy',
    logo: LOGO('fieldy.svg'),
    color: '#6E56CF',
    category: 'assistant',
    authType: 'webhook_token',
    connectPath: '/api/admin/connectors/fieldy/connect',
    statusKey: 'fieldy',
    webhookPath: '/api/webhooks/fieldy', // shown to paste into the Fieldy app
    docsUrl: null,
  },
];

// id → connector, for quick lookups (e.g. tile source marks).
export const CONNECTOR_BY_ID = CONNECTORS.reduce((m, c) => { m[c.id] = c; return m; }, {});

// Status pill styling — matches the OS palette. 'attention' = amber warning.
export const STATUS_META = {
  connected:    { label: 'Connected',      color: '#54D197', dot: '#54D197' },
  disconnected: { label: 'Not connected',  color: '#A7ABB2', dot: 'rgba(255,255,255,0.35)' },
  attention:    { label: 'Needs attention', color: '#F2B755', dot: '#F2B755' },
};
